import type {
  ColorOverflow,
  GlobalSettings,
  Media,
} from "@services/wordpress/wordpress.types";
import CaretIcon from "@icons/caret-icon";
import classnames from "classnames";
import ToggleButton from "../ToggleButton/index.tsx";
import Icon from "@icons/index.tsx";
import type { States } from "@services/filter-api/types";
import { headerLinks, expandedHeaderLinks } from "./links.ts";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";

// Custom hook for cookie management
const useCookie = (name: string) => {
  const [value, setValue] = useState<string | null>(null);

  useEffect(() => {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
    setValue(cookie || null);
  }, [name]);

  return value;
};

// Custom hook for accordion behavior
const useAccordion = () => {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [openSubItem, setOpenSubItem] = useState<string | null>(null);

  const toggleItem = (itemName: string) => {
    setOpenItem(openItem === itemName ? null : itemName);
    // Close submenu when main menu closes
    if (openItem === itemName) {
      setOpenSubItem(null);
    }
  };

  const toggleSubItem = (subItemName: string) => {
    setOpenSubItem(openSubItem === subItemName ? null : subItemName);
  };

  return { openItem, toggleItem, openSubItem, toggleSubItem };
};

// Custom hook for submenu management
const useSubmenuManagement = (isMobile: boolean, openSubItem: string | null, submenuRefs: React.MutableRefObject<Map<string, HTMLElement>>) => {
  const navRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    if (!navRef.current && openSubItem) {
      const visibleSubmenu = submenuRefs.current.get(openSubItem);
      if (visibleSubmenu) {
        navRef.current = visibleSubmenu.closest('[data-hamburger-menu-nav]') as HTMLElement;
      }
    }
    
    const nav = navRef.current;
    if (!nav) return;

    if (openSubItem) {
      // A submenu is open
      const visibleSubmenu = submenuRefs.current.get(openSubItem);
      if (!visibleSubmenu) return;

      nav.classList.add('header-hamburger-menu__nav--sub-menu-visible');
      nav.scrollTop = 0;
      
      // Only perform height calculations on desktop
      if (!isMobile) {
        const originalHeight = nav.offsetHeight;
        const submenuHeight = visibleSubmenu.offsetHeight;
        
        if (submenuHeight < originalHeight) {
          nav.style.height = `${submenuHeight}px`;
        }
      }
    } else {
      nav.classList.remove('header-hamburger-menu__nav--sub-menu-visible');
      nav.scrollTop = 0;
      
      // Only reset height on desktop
      if (!isMobile) {
        nav.style.height = 'max-content';
      }
    }
  }, [isMobile, openSubItem, submenuRefs]);

  // Cleanup refs on unmount
  useEffect(() => {
    return () => {
      submenuRefs.current.clear();
      navRef.current = null;
    };
  }, [submenuRefs]);
};

const Links = ({
  colorOverflow,
  globalSettings,
  headerLogo,
  isMobile,
  navWithFilters,
  states,
}: {
  colorOverflow?: ColorOverflow,
  globalSettings?: GlobalSettings,
  headerLogo?: Media,
  isMobile?: boolean,
  navWithFilters?: boolean,
  states?: States,
}) => {
  const stickyNavExpandedVariation = useCookie('sticky_nav_variation_expanded');
  const { openItem, toggleItem, openSubItem, toggleSubItem } = useAccordion();
  
  // Create refs for submenu elements
  const submenuRefs = useRef<Map<string, HTMLElement>>(new Map());
  
  // Handle submenu management (class and height)
  useSubmenuManagement(!!isMobile, openSubItem, submenuRefs);
  
  // Apply focus trap to open third-level submenu
  useEffect(() => {
    if (!openSubItem) return;

    const thirdLevelContainer = submenuRefs.current.get(openSubItem);
    if (!thirdLevelContainer) return;

    const focusableElements = thirdLevelContainer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab: going backwards
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: going forwards
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    thirdLevelContainer.addEventListener('keydown', handleKeyDown);
    
    // Focus the first element when trap becomes active
    firstElement.focus();

    return () => {
      thirdLevelContainer.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSubItem]);

  // Memoize effective links to prevent unnecessary recalculations
  const effectiveLinks = useMemo(() => {
    let links = globalSettings?.headerLinks;
    
    // Only check cookie for sticky nav variation if navWithFilters is true
    if (navWithFilters) {
      links = stickyNavExpandedVariation === "true" 
        ? expandedHeaderLinks(states)
        : headerLinks()
    }
    
    return links;
  }, [globalSettings?.headerLinks, navWithFilters, stickyNavExpandedVariation, states]);

  // Memoize the login links to prevent unnecessary recalculations
  const loginLinks = useMemo(() => {
    if (!globalSettings?.log_in_links || globalSettings.log_in_links.length === 0) {
      return null;
    }
    
    return globalSettings.log_in_links.map((l) => {
      let providerUrl;
      if (l?.link?.url.includes("provider")) {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'growtherapy.com';

        providerUrl = hostname === "growtherapy.com"
            ? l?.link?.url
            : l?.link?.url.replace(
                "growtherapy.com",
                "growtherapystaging.com"
              );
      }

      return (
        <li>
          <a
            class={classnames("header-hamburger-menu__nav-sublink-link", {
              ["nav-client-login flex gap-2 items-center"]:
                l?.link?.url.includes("client"),
              ["nav-provider-login flex gap-2 items-center"]:
                l?.link?.url.includes("provider"),
            })}
            href={
              l?.link?.url.includes("provider")
                ? providerUrl
                : l?.link?.url
            }
          >
            {l?.link?.title}
          </a>
        </li>
      );
    });
  }, [globalSettings?.log_in_links]);

  return (
    <>
      {effectiveLinks && effectiveLinks.map(({ linkLabel, linkUrl, sublinks }) => {
        const isOpen = openItem === linkLabel;
        
        return (
            <li class="header-hamburger-menu__nav-list-item">
              {sublinks && sublinks.length > 0 ? (
                <>
                  <input
                    name={linkLabel}
                    id={`${linkLabel}-${isMobile ? 'mobile' : 'desktop'}`}
                    class="header-hamburger-menu__toggle-input"
                    type="checkbox"
                    checked={isOpen}
                    onChange={() => toggleItem(linkLabel)}
                    aria-hidden="true"
                  />
                  <label
                    tabindex={0}
                    for={`${linkLabel}-${isMobile ? 'mobile' : 'desktop'}`}
                    class="header__nav-link-text header__nav-link-text--toggle"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleItem(linkLabel);
                      }
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleItem(linkLabel);
                    }}
                  >
                    {linkLabel}
                    <CaretIcon className="header-hamburger-menu__caret" />
                  </label>
                  <ul
                    class={classnames("header-hamburger-menu__nav-sublink", {
                      "bg-gt-cream desktop-nav:bg-gt-cream-light": colorOverflow?.header === "none",
                      "bg-gt-lilac":
                        colorOverflow?.header === "lilac",
                      "header-hamburger-menu__nav-sublink--open": isOpen,
                    })}
                  >
                    {sublinks.map(({ sublink, sublinkIcon }) => (
                      <li>
                        {sublink.sublinks ? (
                          <>
                            <input
                              name={sublink.title}
                              id={`${sublink.title}-${isMobile ? 'mobile' : 'desktop'}`}
                              class="header-hamburger-menu__toggle-input header-hamburger-menu__toggle-input--sub-menu"
                              type="checkbox"
                              checked={openSubItem === sublink.title}
                              onChange={() => toggleSubItem(sublink.title)}
                              aria-hidden="true"
                            />
                            <label
                              tabindex={0}
                              for={`${sublink.title}-${isMobile ? 'mobile' : 'desktop'}`}
                              class="header-hamburger-menu__sub-menu-toggle"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleSubItem(sublink.title);
                                }
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleSubItem(sublink.title);
                              }}
                            >
                              {sublink.title}
                              <CaretIcon className="header-hamburger-menu__caret header-hamburger-menu__caret--right" />
                            </label>
                            <div
                              ref={(el) => el && submenuRefs.current.set(sublink.title, el)}
                              class={classnames("header-hamburger-menu__nav-sublink header-hamburger-menu__nav-sublink--sub-menu", {
                                "header-hamburger-menu__nav-sublink--sub-menu--hidden": openSubItem !== sublink.title
                              })}
                            >
                              <input
                                name={sublink.title}
                                id={`${sublink.title}-${isMobile ? 'mobile' : 'desktop'}`}
                                class="header-hamburger-menu__toggle-input"
                                type="checkbox"
                                aria-hidden="true"
                              />
                              <label
                                tabindex={0}
                                for={`${sublink.title}-${isMobile ? 'mobile' : 'desktop'}`}
                                class="header-hamburger-menu__sub-menu-title"
                                role="button"
                                aria-label={`Back to ${linkLabel} menu`}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleSubItem(sublink.title);
                                    // Focus the parent submenu toggle
                                    const parentToggle = document.querySelector(`label[for="${sublink.title}-${isMobile ? 'mobile' : 'desktop'}"]`) as HTMLLabelElement;
                                    if (parentToggle) {
                                      setTimeout(() => parentToggle.focus(), 0);
                                    }
                                  }
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSubItem(sublink.title);
                                  // Focus the parent submenu toggle
                                  const parentToggle = document.querySelector(`label[for="${sublink.title}-${isMobile ? 'mobile' : 'desktop'}"]`) as HTMLLabelElement;
                                  if (parentToggle) {
                                    setTimeout(() => parentToggle.focus(), 0);
                                  }
                                }}
                              >
                                {isMobile ? (
                                  <>
                                    <CaretIcon className="header-hamburger-menu__caret header-hamburger-menu__caret--left" />
                                    <a href="/">
                                      <img
                                        class="header__logo"
                                        src={headerLogo?.source_url}
                                        alt="Grow Therapy Logo"
                                      />
                                    </a>
                                    <ToggleButton />
                                  </>
                                ) : (
                                  <span class="header-hamburger-menu__sub-menu-title-text">
                                    <CaretIcon className="header-hamburger-menu__caret header-hamburger-menu__caret--left" /> Back
                                  </span>
                                )}
                              </label>
                              <ul class="header-hamburger-menu__sub-menu-list">
                                <li class="header-hamburger-menu__sub-menu-list-title">
                                  {sublink.title}
                                </li>
                                {sublink.sublinks.map(({ title, url }) => (
                                  <li>
                                    <a href={url}>{title}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        ) : (
                          <a
                            class="header-hamburger-menu__nav-sublink-link"
                            href={sublink.url}
                          >
                            {sublinkIcon && <Icon icon={sublinkIcon} />}
                            {" "}
                            {sublink.title}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <a class="header__nav-link-text" href={linkUrl}>{linkLabel}</a>
              )}
            </li>
        );
      })}

      <li class="header-hamburger-menu__nav-list-item">
        <input
          id={`log-in-${isMobile ? 'mobile' : 'desktop'}`}
          name="log-in"
          class="header-hamburger-menu__toggle-input"
          type="checkbox"
          checked={openItem === 'log-in'}
          onChange={() => toggleItem('log-in')}
          aria-hidden="true"
        />
        <label
          tabindex={0}
          for={`log-in-${isMobile ? 'mobile' : 'desktop'}`}
          class="header__nav-link-text header__nav-link-text--toggle"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleItem('log-in');
            }
          }}
          onClick={(e) => {
            e.preventDefault();
            toggleItem('log-in');
          }}
        >
          Log in{" "}
          <CaretIcon className="header-hamburger-menu__caret" />
        </label>
        <ul
          class={classnames("header-hamburger-menu__nav-sublink", {
            "bg-gt-cream desktop-nav:bg-gt-cream-light": colorOverflow?.header === "none",
            "bg-gt-lilac": colorOverflow?.header === "lilac",
            "header-hamburger-menu__nav-sublink--open": openItem === 'log-in',
          })}
        >
          {loginLinks}
        </ul>
      </li>

      <li class="header-hamburger-menu__nav-list-item">
        <a
          className={classnames("nav-primary-cta", {
            "bg-gt-lilac": colorOverflow?.header === "none",
            "bg-gt-cream": colorOverflow?.header === "lilac",
            "header__nav-link-text": navWithFilters,
          })}
          href={globalSettings?.header_cta?.url ?? ""}
        >
          {globalSettings?.header_cta?.title ?? ""}
        </a>
      </li>
    </>
  );
}

export default Links;
